<div class="container mx-auto space-y-6 px-4 py-6">
  <!-- ═══════════ Header ═══════════ -->
  <header class="flex flex-wrap items-start justify-between gap-3">
    <div>
      <h1 hlmH3>Gutschein-Produkte</h1>
      <p hlmMuted class="mt-1">Verwalte deine Gutschein-Produkte und Services</p>
    </div>

    <div class="flex items-center gap-2">
      @if (givesDiscounts) {
        <button hlmBtn variant="outline" [disabled]="loading" (click)="openGlobalDiscountModal()">
          <ng-icon name="lucideTag" />
          Rabatt verwalten
        </button>
      }
      <button id="section-products-create" hlmBtn [disabled]="loading" (click)="openForm()">
        <ng-icon name="lucidePlus" />
        Neues Produkt
      </button>
    </div>
  </header>

  <!-- ═══════════ Messages ═══════════ -->
  @if (error) {
    <div hlmAlert variant="destructive">
      <ng-icon name="lucideTriangleAlert" />
      <p hlmAlertTitle>{{ error }}</p>
      <div hlmAlertAction>
        <button
          hlmBtn
          variant="ghost"
          size="icon-sm"
          aria-label="Schließen"
          (click)="dismissMessage()">
          <ng-icon name="lucideX" />
        </button>
      </div>
    </div>
  }

  @if (success) {
    <div hlmAlert>
      <ng-icon name="lucideCircleCheck" />
      <p hlmAlertTitle>{{ success }}</p>
      <div hlmAlertAction>
        <button
          hlmBtn
          variant="ghost"
          size="icon-sm"
          aria-label="Schließen"
          (click)="dismissMessage()">
          <ng-icon name="lucideX" />
        </button>
      </div>
    </div>
  }

  <!-- ═══════════ Voucher kinds ═══════════ -->
  <section id="section-products-activate" hlmCard>
    <div hlmCardHeader>
      <h2 hlmCardTitle>Gutschein-Arten</h2>
      <p hlmCardDescription>Wähle aus, welche Arten von Gutscheinen deine Kunden kaufen können.</p>
    </div>

    <div hlmCardContent class="grid gap-3 md:grid-cols-2">
      <div hlmItem variant="outline" class="items-start">
        <div hlmItemContent>
          <span hlmItemTitle>Wert-Gutscheine mit freiem Betrag</span>
          <span hlmItemDescription>
            {{
              moneyVoucherEnabled
                ? 'Deine Kunden können Gutscheine mit frei wählbarem Betrag kaufen.'
                : 'Deine Kunden können keine Gutscheine mit frei wählbarem Betrag kaufen.'
            }}
          </span>
        </div>
        <div hlmItemActions>
          <hlm-switch
            inputId="money_voucher_enabled"
            aria-label="Wert-Gutscheine verkaufen"
            [checked]="moneyVoucherEnabled"
            [disabled]="togglingMoneyVoucher"
            (checkedChange)="moneyVoucherEnabled = $event; toggleMoneyVoucher($event)" />
        </div>
      </div>

      <div hlmItem variant="outline" class="items-start">
        <div hlmItemContent>
          <span hlmItemTitle>Produkt-Gutscheine</span>
          <span hlmItemDescription>
            {{
              productsLive
                ? 'Deine Kunden können jetzt Gutscheine für deine konfigurierten Produkte kaufen.'
                : 'Du kannst deine Produkte in Ruhe vorbereiten und aktivieren, wenn du bereit bist.'
            }}
          </span>
        </div>
        <div hlmItemActions>
          <hlm-switch
            inputId="voucher_products_enabled"
            aria-label="Produkt-Gutscheine verkaufen"
            [checked]="productsLive"
            [disabled]="togglingLive"
            (checkedChange)="productsLive = $event; toggleProductsLive($event)" />
        </div>
      </div>
    </div>
  </section>

  <!-- ═══════════ Products ═══════════ -->
  <hlm-tabs [tab]="showArchived ? 'archived' : 'active'" class="w-full">
    <hlm-tabs-list class="w-fit">
      <button hlmTabsTrigger="active" (click)="showArchived = false">
        Aktive Produkte
        <span hlmBadge variant="secondary" class="ms-1.5">{{ products.length }}</span>
      </button>
      <button hlmTabsTrigger="archived" (click)="showArchived = true">
        Archiv
        <span hlmBadge variant="secondary" class="ms-1.5">{{ archivedProducts.length }}</span>
      </button>
    </hlm-tabs-list>

    <div hlmTabsContent="active" class="space-y-3">
      @if (loading && products.length === 0) {
        @for (i of [1, 2, 3]; track i) {
          <hlm-skeleton class="h-40 w-full rounded-xl" />
        }
      } @else if (products.length === 0) {
        <hlm-empty class="py-12">
          <div hlmEmptyHeader>
            <div hlmEmptyMedia variant="icon"><ng-icon name="lucidePackage" /></div>
            <p hlmEmptyTitle>Keine Produkte</p>
            <p hlmEmptyDescription>Erstelle dein erstes Gutschein-Produkt.</p>
          </div>
          <button hlmBtn (click)="openForm()">
            <ng-icon name="lucidePlus" />
            Neues Produkt
          </button>
        </hlm-empty>
      } @else {
        @for (product of products; track product.public_id; let i = $index) {
          <app-product-card
            [product]="product"
            [isArchived]="false"
            [loading]="loading"
            [position]="i + 1"
            [isFirst]="i === 0"
            [isLast]="i === products.length - 1"
            [getImageUrl]="getImageUrl.bind(this)"
            [formatPrice]="formatPrice.bind(this)"
            [isDownloadingExample]="downloadingExampleProductId === product.public_id"
            [attr.data-product-id]="product.public_id"
            (edit)="openForm($event)"
            (toggleStatus)="toggleProductStatus($event)"
            (archive)="archiveProduct($event)"
            (moveLeft)="moveProductLeft($event)"
            (moveRight)="moveProductRight($event)"
            (viewVersionHistory)="openVersionHistory($event)"
            (downloadExample)="downloadProductExample($event)" />
        }
      }
    </div>

    <div hlmTabsContent="archived" class="space-y-3">
      @if (archivedProducts.length === 0) {
        <hlm-empty class="py-12">
          <div hlmEmptyHeader>
            <div hlmEmptyMedia variant="icon"><ng-icon name="lucideArchive" /></div>
            <p hlmEmptyTitle>Keine archivierten Produkte</p>
            <p hlmEmptyDescription>Archivierte Produkte werden hier angezeigt.</p>
          </div>
        </hlm-empty>
      } @else {
        @for (product of archivedProducts; track product.public_id) {
          <app-product-card
            [product]="product"
            [isArchived]="true"
            [loading]="loading"
            [getImageUrl]="getImageUrl.bind(this)"
            [formatPrice]="formatPrice.bind(this)"
            [isDownloadingExample]="downloadingExampleProductId === product.public_id"
            (unarchive)="unarchiveProduct($event)"
            (downloadExample)="downloadProductExample($event)" />
        }
      }
    </div>
  </hlm-tabs>
</div>

<!-- ═══════════ Product form ═══════════ -->
<hlm-dialog [state]="showForm ? 'open' : 'closed'" (stateChanged)="onFormDialogState($event)">
  <hlm-dialog-content *hlmDialogPortal class="max-h-[90vh] max-w-2xl overflow-y-auto">
    <!-- Crop in place instead of stacking a second dialog. -->
    @if (showCropperModal) {
      <app-image-cropper-modal
        inline
        [imageFile]="selectedFileForCropping"
        [existingCropData]="cropperStartData"
        (cropComplete)="onCropComplete($event)"
        (cancel)="onCropCancel()" />
    } @else {
      <div hlmDialogHeader>
        <h3 hlmDialogTitle>{{ editingProduct ? 'Produkt bearbeiten' : 'Neues Produkt' }}</h3>
        <p hlmDialogDescription>
          Name, Preis und Bild bestimmen, wie das Produkt im Shop erscheint.
        </p>
      </div>

      <form [formGroup]="productForm" (ngSubmit)="onSubmit()" class="space-y-4">
        <div hlmField>
          <label hlmFieldLabel for="name">Produktname *</label>
          <input
            hlmInput
            id="name"
            type="text"
            formControlName="name"
            placeholder="z. B. 1-Stunden Massage"
            [attr.aria-invalid]="isInvalid('name') ? 'true' : null" />
          @if (isInvalid('name')) {
            <p hlmFieldDescription class="text-destructive">
              @if (productForm.get('name')?.errors?.['required']) {
                Name ist erforderlich
              } @else {
                Name darf maximal 70 Zeichen haben
              }
            </p>
          }
        </div>

        <div hlmField>
          <div class="flex items-center justify-between">
            <label hlmFieldLabel for="description">Beschreibung</label>
            <span
              class="text-xs"
              [class.text-destructive]="getEffectiveCharCount() > 600"
              [class.text-muted-foreground]="getEffectiveCharCount() <= 600">
              {{ getEffectiveCharCount() }} / 600
            </span>
          </div>
          <textarea
            hlmTextarea
            id="description"
            rows="6"
            formControlName="description"
            placeholder="Beschreibe dein Produkt… (max. 10 Zeilen)"
            class="font-mono"
            [attr.aria-invalid]="getEffectiveCharCount() > 600 ? 'true' : null"
            (input)="productForm.get('description')?.updateValueAndValidity()"></textarea>
          <p hlmFieldDescription>
            Lange Zeilen zählen mehrfach (je 60 Zeichen = 1 Zeile, max. 10 Zeilen).
          </p>
        </div>

        <div class="grid gap-4 sm:grid-cols-2">
          <div hlmField>
            <label hlmFieldLabel for="price">Preis (€) *</label>
            <input
              hlmInput
              id="price"
              type="text"
              inputmode="decimal"
              formControlName="price"
              placeholder="z. B. 49,99"
              [attr.aria-invalid]="isInvalid('price') ? 'true' : null" />
            @if (isInvalid('price')) {
              <p hlmFieldDescription class="text-destructive">
                @if (productForm.get('price')?.errors?.['required']) {
                  Preis ist erforderlich
                } @else {
                  Bitte gib einen gültigen Preis ein (z. B. 49,99)
                }
              </p>
            }
          </div>

          <!-- A network pays its members the face value, so it gives no discounts. -->
          @if (givesDiscounts) {
            <div hlmField>
              <label hlmFieldLabel for="discount_percentage">Rabatt (%)</label>
              <input
                hlmInput
                id="discount_percentage"
                type="number"
                min="0"
                max="50"
                step="1"
                inputmode="numeric"
                formControlName="discount_percentage"
                placeholder="0"
                [attr.aria-invalid]="isInvalid('discount_percentage') ? 'true' : null" />
              @if (isInvalid('discount_percentage')) {
                <p hlmFieldDescription class="text-destructive">
                  Der Rabatt muss zwischen 0 und 50 % liegen
                </p>
              } @else if (discountedPricePreview() !== null) {
                <p hlmFieldDescription>
                  Kund:innen zahlen
                  <span class="text-foreground font-medium">
                    {{ productService.formatPrice(discountedPricePreview()!) }}
                  </span>
                  – der Gutschein bleibt in voller Höhe einlösbar.
                </p>
              }
            </div>
          }
        </div>

        <!-- Image -->
        <div hlmField>
          <label hlmFieldLabel>Produktbild</label>

          @if (previewUrl) {
            <div class="flex items-start gap-3">
              <div class="relative">
                <img
                  [src]="previewUrl"
                  alt="Vorschau"
                  class="border-border h-32 w-auto rounded-lg border" />
                <button
                  hlmBtn
                  type="button"
                  variant="destructive"
                  size="icon-xs"
                  class="absolute -end-2 -top-2 rounded-full"
                  aria-label="Bild entfernen"
                  (click)="
                    editingProduct && !selectedFile ? markImageForDeletion() : removeImage()
                  ">
                  <ng-icon name="lucideX" />
                </button>
              </div>

              @if (selectedFile || (editingProduct?.image_path && !imageToDelete)) {
                <button hlmBtn type="button" variant="outline" size="sm" (click)="editImage()">
                  <ng-icon name="lucideCrop" />
                  Neu zuschneiden
                </button>
              }
            </div>
          }

          @if (imageToDelete && !selectedFile) {
            <div hlmAlert variant="warning">
              <ng-icon name="lucideTriangleAlert" />
              <p hlmAlertTitle>Bild wird beim Speichern gelöscht</p>
            </div>
          }

          <input
            type="file"
            id="imageUpload"
            class="hidden"
            accept=".jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp"
            (change)="onFileSelected($event)" />
          <label hlmBtn variant="outline" size="sm" for="imageUpload" class="w-fit cursor-pointer">
            <ng-icon name="lucideImagePlus" />
            Bild auswählen
          </label>
          <p hlmFieldDescription>Nur JPG, PNG, GIF oder WEBP (max. 5 MB)</p>
          @if (imageError) {
            <hlm-field-error forceShow>{{ imageError }}</hlm-field-error>
          }
        </div>

        <div hlmItem variant="muted">
          <div hlmItemContent>
            <span hlmItemTitle>Produkt ist aktiv</span>
            <span hlmItemDescription>Inaktive Produkte erscheinen nicht im Shop.</span>
          </div>
          <div hlmItemActions>
            <hlm-switch
              aria-label="Produkt ist aktiv"
              [checked]="!!productForm.get('is_active')?.value"
              (checkedChange)="productForm.get('is_active')?.setValue($event)" />
          </div>
        </div>

        @if (formError) {
          <div hlmAlert variant="destructive">
            <ng-icon name="lucideTriangleAlert" />
            <p hlmAlertTitle>{{ formError }}</p>
            <p hlmAlertDescription>Deine Eingaben sind erhalten – versuche es bitte erneut.</p>
          </div>
        }

        <div hlmDialogFooter>
          <button hlmBtn type="button" variant="outline" [disabled]="loading" (click)="closeForm()">
            Abbrechen
          </button>
          <button hlmBtn type="submit" [disabled]="loading || productForm.invalid">
            @if (loading) {
              <hlm-spinner />
              Wird gespeichert…
            } @else {
              {{ editingProduct ? 'Aktualisieren' : 'Erstellen' }}
            }
          </button>
        </div>
      </form>
    }
  </hlm-dialog-content>
</hlm-dialog>

<!-- ═══════════ Version history ═══════════ -->
<hlm-dialog
  [state]="showVersionHistory ? 'open' : 'closed'"
  (stateChanged)="onVersionHistoryDialogState($event)">
  <hlm-dialog-content *hlmDialogPortal class="max-h-[90vh] max-w-3xl overflow-y-auto">
    <div hlmDialogHeader>
      <h3 hlmDialogTitle>Versionshistorie</h3>
      @if (versionHistoryProduct) {
        <p hlmDialogDescription>{{ versionHistoryProduct.name }}</p>
      }
    </div>

    @if (loadingVersionHistory) {
      <div class="space-y-3">
        @for (i of [1, 2]; track i) {
          <hlm-skeleton class="h-28 w-full rounded-lg" />
        }
      </div>
    } @else if (versionHistory.length === 0) {
      <hlm-empty class="py-8">
        <div hlmEmptyHeader>
          <div hlmEmptyMedia variant="icon"><ng-icon name="lucideHistory" /></div>
          <p hlmEmptyTitle>Keine Versionen gefunden</p>
        </div>
      </hlm-empty>
    } @else {
      <div hlmItemGroup class="gap-2">
        @for (version of versionHistory; track version.version_id) {
          <div hlmItem variant="outline" class="items-start">
            @if (version.image_cropped_path || version.image_path) {
              <div hlmItemMedia variant="image">
                <img
                  [src]="
                    productService.getImageUrl(
                      version.image_cropped_path || version.image_path,
                      version.created_at
                    )
                  "
                  alt="" />
              </div>
            }

            <div hlmItemContent>
              <span hlmItemTitle>
                {{ formatVersionNumber(version) }}
                <span hlmBadge [variant]="version.is_current ? 'solid' : 'outline'">
                  {{ version.is_current ? 'Aktuell' : 'Historisch' }}
                </span>
              </span>
              <span hlmItemDescription>{{ version.name }}</span>
              @if (version.description) {
                <span hlmItemDescription>{{ version.description }}</span>
              }
              @if (version.change_summary) {
                <span hlmItemDescription>Änderungen: {{ version.change_summary }}</span>
              }
            </div>

            <div hlmItemActions class="flex-col items-end gap-1">
              <span class="text-sm font-medium tabular-nums">
                {{ productService.formatPrice(version.price) }}
              </span>
              @if (version.discount_percentage > 0) {
                <span hlmBadge variant="secondary">−{{ version.discount_percentage }}%</span>
              }
              <span class="text-muted-foreground text-xs">
                {{ version.purchase_count || 0 }} verkauft
              </span>
              <span class="text-muted-foreground text-xs">
                {{ version.created_at | date: 'dd.MM.yyyy HH:mm' }}
              </span>
            </div>
          </div>
        }
      </div>
    }

    <div hlmDialogFooter>
      <button hlmBtn variant="outline" (click)="closeVersionHistory()">Schließen</button>
    </div>
  </hlm-dialog-content>
</hlm-dialog>

<!-- ═══════════ Toggle confirmation ═══════════ -->
<hlm-alert-dialog
  [state]="pendingToggleAction ? 'open' : 'closed'"
  (stateChanged)="onToggleDialogState($event)">
  <hlm-alert-dialog-content *hlmAlertDialogPortal class="max-h-[90vh] max-w-2xl overflow-y-auto">
    <div hlmAlertDialogHeader>
      <h3 hlmAlertDialogTitle>{{ toggleConfirmTitle }}</h3>
      @if (toggleConfirmSubtitle) {
        <p hlmAlertDialogDescription>{{ toggleConfirmSubtitle }}</p>
      }
    </div>

    @if (pendingToggleAction === 'products-activate') {
      <div class="space-y-4 text-sm">
        <div hlmAlert>
          <ng-icon name="lucideCircleCheck" />
          <p hlmAlertTitle>Produkt-Gutscheine sind universell einlösbar</p>
          <p hlmAlertDescription>
            Jeder Produkt-Gutschein kann für jedes beliebige Produkt oder jede Dienstleistung in
            deinem Geschäft eingelöst werden – unter Berücksichtigung des jeweiligen Gutscheinwerts.
          </p>
        </div>

        <div>
          <h4 hlmSmall>Warum ist das so?</h4>
          <p hlmMuted class="mt-1">
            Diese Regelung ergibt sich aus den steuerrechtlichen Bestimmungen für
            Mehrzweck-Gutscheine. Einzweck-Gutscheine erfordern eine komplexere steuerliche
            Handhabung und sind daher nicht verfügbar.
          </p>
        </div>

        <div hlmItem variant="muted" class="flex-col items-start gap-1">
          <span hlmItemTitle>Beispiel</span>
          <p hlmMuted>
            Ein Kunde kauft einen Gutschein für „Massage 60 Min“ im Wert von 80 €. Dieser Gutschein
            kann auch für andere Leistungen wie „Gesichtsbehandlung“ oder „Maniküre“ verwendet
            werden.
          </p>
          <p hlmMuted>
            Bei Überschreitung des Werts wird der Gutscheinwert angerechnet: Bei einer Leistung im
            Wert von 120 € werden die 80 € des Gutscheins angerechnet, der Kunde zahlt die Differenz
            von 40 €.
          </p>
        </div>
      </div>
    } @else {
      <p hlmAlertDialogDescription>{{ toggleConfirmMessage }}</p>
    }

    <div hlmAlertDialogFooter>
      <button hlmAlertDialogCancel hlmBtn variant="outline" (click)="cancelToggleConfirm()">
        Abbrechen
      </button>
      <button
        hlmAlertDialogAction
        hlmBtn
        [variant]="toggleConfirmIsDeactivation ? 'destructive' : 'default'"
        (click)="confirmToggle()">
        {{ toggleConfirmButtonText }}
      </button>
    </div>
  </hlm-alert-dialog-content>
</hlm-alert-dialog>

<!-- ═══════════ Global discount ═══════════ -->
<hlm-dialog
  [state]="showGlobalDiscountModal ? 'open' : 'closed'"
  (stateChanged)="onDiscountDialogState($event)">
  <hlm-dialog-content *hlmDialogPortal class="max-w-lg">
    <div hlmDialogHeader>
      <h3 hlmDialogTitle>Rabatt für alle Produkte</h3>
      <p hlmDialogDescription>
        Der Gutschein bleibt in voller Höhe einlösbar – dein Unternehmen trägt die Differenz.
      </p>
    </div>

    <div class="space-y-4">
      <div hlmField>
        <label hlmFieldLabel for="global-discount">Rabatt (%)</label>
        <input
          hlmInput
          id="global-discount"
          type="number"
          min="0"
          max="50"
          step="1"
          inputmode="numeric"
          class="w-32"
          [(ngModel)]="globalDiscount"
          [disabled]="applyingGlobalDiscount" />
      </div>

      <label class="flex cursor-pointer items-start gap-2">
        <hlm-checkbox
          class="mt-0.5"
          [checked]="discountAcknowledged"
          [disabled]="applyingGlobalDiscount"
          (checkedChange)="discountAcknowledged = $event" />
        <span class="text-sm">
          Mir ist bewusst, dass meine Kund:innen den reduzierten Preis zahlen, der Gutschein aber
          weiterhin in voller Höhe einlösbar ist und mein Unternehmen die Differenz trägt.
        </span>
      </label>

      @if (globalDiscountSuccess) {
        <div hlmAlert>
          <ng-icon name="lucideCircleCheck" />
          <p hlmAlertTitle>{{ globalDiscountSuccess }}</p>
        </div>
      }
      @if (globalDiscountError) {
        <div hlmAlert variant="destructive">
          <ng-icon name="lucideTriangleAlert" />
          <p hlmAlertTitle>{{ globalDiscountError }}</p>
        </div>
      }
    </div>

    <div hlmDialogFooter class="sm:justify-between">
      <button
        hlmBtn
        variant="ghost"
        [disabled]="applyingGlobalDiscount"
        (click)="removeAllDiscounts()">
        Alle Rabatte entfernen
      </button>
      <button hlmBtn [disabled]="!canApplyGlobalDiscount" (click)="applyGlobalDiscount()">
        @if (applyingGlobalDiscount) {
          <hlm-spinner />
          Wird angewendet…
        } @else {
          Auf alle Produkte anwenden
        }
      </button>
    </div>
  </hlm-dialog-content>
</hlm-dialog>

<!-- Voucher widget -->
@if (company) {
  <div class="voucher-widget-fixed">
    <schenkwerk-voucher [storeId]="getStoreId()" [asWidget]="true"></schenkwerk-voucher>
  </div>
}
